import { useEffect } from "react";

/**
 * Lightweight SEO head manager. No dependencies.
 * Only used on PUBLIC pages.
 *
 * Props:
 *   title:           Page title
 *   description:     Meta description (≤ 160 chars)
 *   canonical:       Absolute canonical URL
 *   keywords:        Comma-separated keywords string (already sanitized)
 *   image:           OG/Twitter image absolute URL
 *   type:            "website" | "article" | "product" (OG type)
 *   lang:            "ar" | "en"
 *   alternates:      { ar?: string, en?: string }   absolute URLs for hreflang
 *   noindex:         boolean — emit robots: noindex,nofollow
 *   jsonLd:          object | object[] — schema.org structured data
 *   siteName:        Brand name for og:site_name
 */
export default function SEO({
  title,
  description,
  canonical,
  keywords,
  image,
  type = "website",
  lang = "ar",
  alternates = {},
  noindex = false,
  jsonLd = null,
  siteName = "iSaloon",
}) {
  useEffect(() => {
    // ── document attributes ─────────────────────────────
    if (lang) {
      document.documentElement.setAttribute("lang", lang);
      document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    }
    if (title) document.title = title;

    // helper to upsert <meta>
    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => v != null && el.setAttribute(k, v));
        el.setAttribute("data-seo", "1");
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => v != null && el.setAttribute(k, v));
      }
    };
    const upsertLink = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        Object.entries(attrs).forEach(([k, v]) => v != null && el.setAttribute(k, v));
        el.setAttribute("data-seo", "1");
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => v != null && el.setAttribute(k, v));
      }
    };

    if (description) upsertMeta('meta[name="description"]', { name: "description", content: description });
    if (keywords) upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    upsertMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });

    // Open Graph
    if (title) upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    if (description) upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    if (canonical) upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    if (image) upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    if (siteName) upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: lang === "ar" ? "ar_QA" : "en_US" });

    // Twitter card
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    if (title) upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    if (description) upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    if (image) upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    // canonical
    if (canonical) upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonical });

    // hreflang
    // Remove any previous SEO-managed hreflangs to avoid stacking
    document.head.querySelectorAll('link[rel="alternate"][data-seo="1"]').forEach((n) => n.remove());
    if (alternates.ar) {
      const link = document.createElement("link");
      link.rel = "alternate"; link.hreflang = "ar"; link.href = alternates.ar;
      link.setAttribute("data-seo", "1"); document.head.appendChild(link);
    }
    if (alternates.en) {
      const link = document.createElement("link");
      link.rel = "alternate"; link.hreflang = "en"; link.href = alternates.en;
      link.setAttribute("data-seo", "1"); document.head.appendChild(link);
    }
    if (alternates.ar || alternates.en) {
      const xd = document.createElement("link");
      xd.rel = "alternate"; xd.hreflang = "x-default";
      xd.href = alternates.en || alternates.ar || canonical || "";
      xd.setAttribute("data-seo", "1");
      if (xd.href) document.head.appendChild(xd);
    }

    // JSON-LD
    document.head.querySelectorAll('script[type="application/ld+json"][data-seo="1"]').forEach((n) => n.remove());
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const obj of arr) {
        if (!obj) continue;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo", "1");
        s.textContent = JSON.stringify(obj);
        document.head.appendChild(s);
      }
    }
  }, [title, description, canonical, keywords, image, type, lang, noindex, jsonLd, siteName, alternates.ar, alternates.en]);

  return null;
}
